import { BaseDevice, Config, ConfigCapability, Role, RoleId } from '@rapoo/device-sdk'
import { useLocaleStore, useUserStore } from '@/stores'
import { storeToRefs } from 'pinia'

/** 是否声明了用户可切换的配置能力。底座恢复出厂也会挂 features.config，不能 features.config 判断。 */
export function hasConfigCapability(device: BaseDevice | null | undefined): boolean {
  return !!device?.manifest.capabilities.some((item) => item.type === 'config')
}

/**
 * 获取当前角色（管理员/普通用户）可见的配置槽位列表
 *
 * 设备 manifest 中 config.configSlots 定义了每个槽位的角色权限，
 * 没有 configSlots 的返回 null（表示不限制可见性）。
 * 管理员只返回 role 包含 superAdmin 或 admin 的槽位，
 * 普通用户只返回 role 包含 user 的槽位。
 */
function getRoleMatchedSlots(device: BaseDevice | null): { id: number; roles: Role[] }[] | null {
  const config = device?.manifest.capabilities.find((item) => item.type === 'config') as
    | ConfigCapability
    | undefined
  const slots = config?.config.configSlots
  // 没有配置槽位约束，全部可见
  if (!slots) return null
  const userStore = useUserStore()
  const { isAdmin } = storeToRefs(userStore)
  const isAdminRole = (roles: Role[]) =>
    roles.includes(RoleId.superAdmin) || roles.includes(RoleId.admin)
  // 管理员只看管理员角色的槽位，普通用户只看 user 角色的槽位
  return slots.filter((slot) =>
    isAdmin.value ? isAdminRole(slot.roles) : slot.roles.includes(RoleId.user),
  )
}

/**
 * 获取配置在角色可见槽位中的显示名称
 * 使用相对位置（第 1 个/第 2 个…）而非绝对 index，避免管理员看到"出厂配置 4"的奇怪计数
 */
function getConfigSlotName(configIndex: number, device: BaseDevice | null): string | null {
  const matchedSlots = getRoleMatchedSlots(device)
  const { t } = useLocaleStore()
  if (matchedSlots) {
    const pos = matchedSlots.findIndex((item) => item.id === configIndex)
    if (pos !== -1) {
      const userStore = useUserStore()
      const { isAdmin } = storeToRefs(userStore)
      if (isAdmin.value) {
        return `${t('home.config.card.defaultName')} ${pos + 1}`
      } else {
        return `${t('home.config.card.userDefaultName')} ${pos + 1}`
      }
    }
  }
  // 不在角色可见槽位中，让调用方继续用后续逻辑处理
  return null
}

/**
 * 检查字符串是否为 SDK 返回的标准化 i18n key 路径。
 * SDK resolveConfigName 已将设备原始命名（如 i18n<defaultOnboard>2）转为
 * 点号路径（如 home.config.card.defaultOnboard2）。
 */
function isI18nKey(name: string): boolean {
  return name.startsWith('home.config.card.')
}

/**
 * 格式化配置的显示名称
 *
 * 优先级：
 * 1. cfg 为空 -> 返回"默认配置"（兜底）
 * 2. cfg.name 以 home.config.card. 开头（SDK 标准化 i18n key）-> 通过 i18n 翻译
 * 3. configIndex 在角色可见槽位中 -> 按相对位置返回"出厂配置 N"或"用户默认配置 N"
 * 4. index === 0 -> 返回"默认配置"
 * 5. 设备不存在 config 能力特征 -> 直接返回 cfg.name
 * 6. 其他情况 -> 返回设备实际保存的名称 cfg.name
 * 7. cfg.name 为空（如 GCOME 板载读出的无名配置）-> 返回"未命名"兜底
 */
export function useConfigName(cfg: Config | undefined | null, device: BaseDevice | null): string {
  const { t } = useLocaleStore()
  // cfg 为空，返回默认名称兜底
  if (!cfg) return t('home.config.card.defaultName')
  // SDK 返回的标准化 i18n key，直接翻译
  if (cfg.name && isI18nKey(cfg.name)) {
    return t(cfg.name)
  }
  // 优先使用角色槽位名称（相对序号），仅在可见时返回
  const slotName = getConfigSlotName(cfg.index, device)
  if (slotName) return slotName
  // index 为 0 的配置固定显示为"默认配置"
  if (cfg.index === 0) {
    return t('home.config.card.defaultName')
  }
  // 设备不支持 config 能力特征时，直接返回保存的名称（空名兜底"未命名"）
  if (device) {
    const config = device.features.config
    if (!config) return cfg.name?.trim() || t('home.config.card.untitled')
  }
  // 返回设备实际保存的名称（空名兜底"未命名"，避免 UI 显示空白）
  return cfg.name?.trim() || t('home.config.card.untitled')
}

/**
 * 判断配置是否已被占用。
 *
 * - presenceBased=true（无 configSlots，如 GCOME）：出现在列表中即占用，空名也算占用
 * - presenceBased=false（有 configSlots）：空名称视为空闲，与固件 createConfig 按空名找槽一致
 */
export function isConfigOccupied(config: Config | undefined, presenceBased = false): boolean {
  if (!config) return false
  if (presenceBased) return true
  return config.name.trim() !== ''
}

/**
 * 生成新配置的默认名称和对应槽位 index
 *
 * 命名规则：按「将被写入的位置」取名，而不是按当前已有数量。
 * 删除中间某个配置后，新增会填回该空位，因此名称也应对应该位置序号。
 * 全部删光后从最小空闲位重新计数（从 1 起）。
 *
 * 有 configSlots 时，找当前角色可见槽位中第一个空闲位，
 * 按相对位置命名为"出厂配置 N"或"用户配置 N"。
 *
 * 没有 configSlots 时，找最小空闲 index，命名为"新配置 N"（index 0 为"默认配置"）。
 */
export function useNewConfigDefaultName(
  device: BaseDevice | null,
  existingConfigs: Config[],
): { name: string; configIndex?: number } {
  const { t } = useLocaleStore()
  const matchedSlots = getRoleMatchedSlots(device)
  // 无槽位设备（GCOME 等）按列表占用；有槽位设备按非空名占用
  const presenceBased = !matchedSlots
  const occupiedIndices = new Set(
    existingConfigs
      .filter((config) => isConfigOccupied(config, presenceBased))
      .map((config) => config.index),
  )
  const userStore = useUserStore()
  const { isAdmin } = storeToRefs(userStore)

  // 有配置槽位约束：按空闲槽位在角色可见列表中的相对位置命名
  if (matchedSlots) {
    for (let i = 0; i < matchedSlots.length; i++) {
      const slot = matchedSlots[i]
      if (occupiedIndices.has(slot.id)) continue
      if (isAdmin.value) {
        return { name: `${t('home.config.card.defaultName')} ${i + 1}`, configIndex: slot.id }
      }
      return {
        name: `${t('home.config.card.userDefaultName')} ${i + 1}`,
        configIndex: slot.id,
      }
    }
  }

  // 没有配置槽位约束：找最小空闲 index（与固件填空位顺序一致）
  let freeIndex = 0
  while (occupiedIndices.has(freeIndex)) {
    freeIndex++
  }

  if (freeIndex === 0) {
    return { name: t('home.config.card.defaultName'), configIndex: 0 }
  }

  return { name: `${t('home.config.new')} ${freeIndex}`, configIndex: freeIndex }
}
